import React from "react";

type VerificationStatus = "loading" | "success" | "error";

type EmailVerificationProps = {
  status: VerificationStatus;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  onErrorAction?: () => void;
  errorActionLabel?: string;
};

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  status,
  loadingMessage = "Verifying your email...",
  successMessage = "Your email has been successfully verified.",
  errorMessage = "Verification failed. Please try again.",
  onErrorAction,
  errorActionLabel = "Try Again",
}) => {
  if (status === "loading") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-solid border-brand-500 border-t-transparent"></div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            {loadingMessage}
          </h2>
          <p className="text-gray-600">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Email Verified
          </h2>
          <p className="text-gray-600">{successMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-8 w-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
          Verification Failed
        </h2>
        <p className="mb-6 text-center text-gray-600">{errorMessage}</p>
        {onErrorAction && (
          <button
            onClick={onErrorAction}
            className="w-full rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
          >
            {errorActionLabel}
          </button>
        )}
      </div>
    </div>
  );
};
